import { Controller } from "@hotwired/stimulus";
import { Stimulus } from "../assets/javascripts/stimulus";
import DropdownComponentController from "./dropdown_component_controller";
import { shouldLaunch } from "./dialog_component/launch";

const IDENTIFIER = "dialog-component";
const HOST = "dialog.dialog-component";
const CANCELABLE_REASONS = ["close-button", "escape", "backdrop"];

// Launchers are invoker command buttons (natively, or through
// invoker_commands.js) and links with data-dialog-component-launcher. The
// controller takes over both commands so it can dispatch its events, and
// adds what <dialog> lacks: backdrop clicks, focus return to a hidden
// launcher's menu, scroll reset and the scrollbar gap. See README.md.
export default class DialogComponentController extends Controller {
  static values = { openOnConnect: Boolean };

  // The controller of the dialog that contains `element`, or null.
  static for(element) {
    const host = element?.closest?.(HOST);
    if (!host) return null;

    const application = window.Stimulus || Stimulus;
    return application.getControllerForElementAndIdentifier(host, IDENTIFIER);
  }

  #trigger = null;
  #announcedOpen = false;
  #hideDispatched = false;
  #closeReason = null;
  #escapePressed = false;
  #downOnBackdrop = false;

  connect() {
    const dialog = this.element;
    dialog.addEventListener("cancel", this.#onCancel);
    dialog.addEventListener("close", this.#onClose);
    dialog.addEventListener("keydown", this.#onKeydown);
    dialog.addEventListener("pointerdown", this.#onPointerdown);
    dialog.addEventListener("click", this.#onClick);
    document.addEventListener("click", this.#onDocumentClick);
    document.addEventListener("turbo:before-cache", this.#onBeforeCache);

    // A non-modal open dialog (server-rendered with `open`, or restored from
    // a snapshot) would sit inline in the page with nothing inert behind it.
    if (dialog.open && !dialog.matches(":modal")) dialog.close();

    if (this.openOnConnectValue) this.open(null);
  }

  disconnect() {
    const dialog = this.element;
    dialog.removeEventListener("cancel", this.#onCancel);
    dialog.removeEventListener("close", this.#onClose);
    dialog.removeEventListener("keydown", this.#onKeydown);
    dialog.removeEventListener("pointerdown", this.#onPointerdown);
    dialog.removeEventListener("click", this.#onClick);
    document.removeEventListener("click", this.#onDocumentClick);
    document.removeEventListener("turbo:before-cache", this.#onBeforeCache);
  }

  get isOpen() {
    return this.element.open;
  }

  // Invoker command buttons, native or from the fallback. The controller
  // runs the command itself so :show and :hide can cancel it.
  command(event) {
    const command = event.command?.toLowerCase();
    if (command === "show-modal") {
      event.preventDefault();
      this.open(event.source ?? null);
    } else if (command === "close") {
      event.preventDefault();
      const source = event.source;
      const value = source?.hasAttribute("value") ? source.value : undefined;
      this.#requestClose("close-button", value);
    }
  }

  open(trigger = null) {
    const dialog = this.element;
    if (this.isOpen || !dialog.isConnected) return;

    // Reopened before the last closing's queued close event arrived: that
    // closing gets its :hidden now, and the event will find the dialog open.
    if (this.#announcedOpen) {
      this.#finalizeClose();
      // A :hidden listener may have opened it, or removed it.
      if (this.isOpen || !dialog.isConnected) return;
    }

    this.#trigger = trigger;
    if (this.#dispatch("show", { cancelable: true }).defaultPrevented) {
      this.#trigger = null;
      return;
    }

    this.#hideDispatched = false;
    this.#closeReason = null;
    this.#escapePressed = false;
    this.#downOnBackdrop = false;

    this.#setScrollbarGap();
    dialog.showModal();
    this.#announcedOpen = true;

    // A dialog keeps its scroll position while closed, so a reopened one
    // would start where the last one was left. Before showModal() it has no
    // box, and setting scrollTop does nothing.
    dialog.scrollTop = 0;
    const body = dialog.querySelector(".dialog-component__body");
    if (body) body.scrollTop = 0;

    this.#dispatch("shown");
  }

  close() {
    this.#requestClose("script");
  }

  #requestClose(reason, returnValue) {
    if (!this.isOpen) return;
    if (this.#announcedOpen) {
      const cancelable = CANCELABLE_REASONS.includes(reason);
      if (!this.#dispatchHide(reason, cancelable)) return;
    }
    this.element.close(returnValue);
  }

  // Escape. Chrome skips cancel when there has been no user activation since
  // the last one, and #onClose covers that.
  #onCancel = (event) => {
    if (this.#announcedOpen && !this.#hideDispatched) {
      if (!this.#dispatchHide("escape", event.cancelable)) {
        event.preventDefault();
      }
    }
    this.#escapePressed = false;
  };

  // The close event is queued as a task, so it can arrive after the dialog
  // was opened again (connect closing an inline dialog, then
  // open_on_connect, or open() finalizing first): only a dialog that is
  // still closed counts.
  #onClose = () => {
    if (this.element.open || !this.#announcedOpen) return;
    this.#finalizeClose();
  };

  // Ends a closing: :hide if nothing dispatched it yet, focus, then
  // :hidden, so a :hidden listener can move focus and keep it there.
  #finalizeClose({ restoreFocus = true } = {}) {
    this.#announcedOpen = false;

    if (!this.#hideDispatched) {
      this.#dispatchHide(this.#escapePressed ? "escape" : "script", false);
    }
    const trigger = this.#trigger;
    const reason = this.#closeReason;
    // Cleared before :hidden, whose listener may open the dialog again.
    this.#trigger = null;
    this.#hideDispatched = false;

    if (restoreFocus) this.#restoreFocus(trigger);
    this.#dispatch("hidden", { reason, trigger });
  }

  // Turbo clones the page right after this event, so the dialog has to be
  // closed, and its events sent, before the listener returns: the native
  // close event arrives too late. Otherwise Back would restore an open dialog
  // with the page scroll locked. An open_on_connect dialog is removed so the
  // restored page doesn't open it again.
  #onBeforeCache = () => {
    if (this.isOpen) {
      this.#dispatchHide("turbo-cache", false);
      this.element.close();
      this.#finalizeClose({ restoreFocus: false });
    } else if (this.#announcedOpen) {
      // Closed in this task: the close event would arrive too late.
      this.#finalizeClose({ restoreFocus: false });
    }
    if (this.openOnConnectValue) this.element.remove();
  };

  #onKeydown = (event) => {
    this.#escapePressed = event.key === "Escape";
  };

  // A click whose press started on the content (a text selection dragged
  // out) lands on the <dialog> too, so only a press on the backdrop counts.
  // The <dialog> scrolls a tall dialog, and a press on its scrollbar targets
  // the <dialog> as well.
  #onPointerdown = (event) => {
    const dialog = this.element;
    this.#downOnBackdrop =
      event.target === dialog && event.offsetX < dialog.clientWidth;
  };

  #onClick = (event) => {
    const fromBackdrop = this.#downOnBackdrop;
    this.#downOnBackdrop = false;
    if (event.target !== this.element || !fromBackdrop) return;
    this.#requestClose("backdrop");
  };

  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest("a[data-dialog-component-launcher]");
    if (link?.dataset.dialogComponentLauncher !== this.element.id) return;
    if (!shouldLaunch(event)) return;

    event.preventDefault();
    this.open(link);
  };

  // The page's scrollbar goes away while a modal dialog is open (see
  // dialog_component.css), and the body is padded by its width so nothing
  // moves. A second dialog over the first would measure no scrollbar.
  #setScrollbarGap() {
    if (document.querySelector(`${HOST}:modal`)) return;
    const root = document.documentElement;
    const gap = window.innerWidth - root.clientWidth;
    root.style.setProperty("--dialog-component-scrollbar-gap", `${gap}px`);
  }

  // The native close has already returned focus to what had it before the
  // dialog opened. A launcher in a menu is hidden by then, so focus goes to
  // the menu's trigger instead.
  #restoreFocus(trigger) {
    if (!trigger) return;
    if (trigger.isConnected && trigger.checkVisibility()) {
      trigger.focus();
      return;
    }

    const dropdown = DropdownComponentController.for(trigger);
    if (
      dropdown?.hasTriggerTarget &&
      dropdown.triggerTarget.checkVisibility()
    ) {
      dropdown.triggerTarget.focus();
    }
  }

  #dispatchHide(reason, cancelable) {
    this.#closeReason = reason;
    this.#hideDispatched = true;
    const event = this.#dispatch("hide", { cancelable, reason });
    if (cancelable && event.defaultPrevented) {
      this.#closeReason = null;
      this.#hideDispatched = false;
      return false;
    }
    return true;
  }

  #dispatch(
    name,
    { cancelable = false, reason = null, trigger = this.#trigger } = {},
  ) {
    return this.dispatch(name, {
      detail: { trigger, reason },
      cancelable,
    });
  }
}
